import { apiClient } from './client';
import {
  Check2FAResponse,
  SendCodeResponse,
  VerifyCodeResponse,
  LoginResponse,
  ForgotPasswordResponse,
  LoginCredentials,
  TwoFactorVerification,
  ForgotPasswordRequest,
  ValidateResetTokenRequest,
  ValidateResetTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '@/types';

/**
 * Check if two-factor authentication is enabled for the user
 */
export const checkTwoFactorEnabled = async (
  credentials: LoginCredentials
): Promise<Check2FAResponse> => {
  const response = await apiClient.post<Check2FAResponse>(
    '/v1/auth/check-2fa',
    credentials
  );
  return response.data;
};

/**
 * Send two-factor authentication code to user's device
 */
export const sendTwoFactorCode = async (
  credentials: LoginCredentials
): Promise<SendCodeResponse> => {
  const response = await apiClient.post<SendCodeResponse>(
    '/v1/auth/send-2fa-code',
    credentials
  );
  return response.data;
};

/**
 * Verify two-factor authentication code
 */
export const verifyTwoFactorCode = async (
  verification: TwoFactorVerification
): Promise<VerifyCodeResponse> => {
  const response = await apiClient.post<VerifyCodeResponse>(
    '/v1/auth/verify-2fa-code',
    verification
  );
  return response.data;
};

/**
 * Perform login via Apigee gateway.
 * Apigee handles:
 *   1. Getting a JWT (access + refresh) from the webserver
 *   2. Creating a Django session (sessionid cookie forwarded back)
 * No CSRF token needed from the UI.
 */
export const login = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/v1/auth/login', {
    email: credentials.email,
    password: credentials.password,
  });
  return response.data;
};

/**
 * Refresh an expired access token using the refresh token
 */
export const refreshToken = async (
  refresh: string
): Promise<{ token: string; expires: string }> => {
  const response = await apiClient.post<{ token: string; expires: string }>(
    '/v1/auth/refresh',
    { refresh }
  );
  return response.data;
};

/**
 * Send password reset email via Apigee (wraps mid-tier GraphQL)
 */
export const sendPasswordResetEmail = async (
  request: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  const response = await apiClient.post<ForgotPasswordResponse>(
    '/v1/auth/forgot-password',
    { email: request.email }
  );
  return response.data;
};

/**
 * Validate a Django password reset token via Apigee.
 * Returns a session_token needed for the actual reset call.
 */
export const validateResetToken = async (
  request: ValidateResetTokenRequest
): Promise<ValidateResetTokenResponse> => {
  const response = await apiClient.post<ValidateResetTokenResponse>(
    '/v1/auth/validate-reset-token',
    request
  );
  return response.data;
};

/**
 * Reset password using a validated session_token from validateResetToken.
 */
export const resetPassword = async (
  request: ResetPasswordRequest
): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<ResetPasswordResponse>(
    '/v1/auth/reset-password',
    request
  );
  return response.data;
};
